/**
 * Keep profile formatting shared across the collaboration page, connection profiles, and onboarding
 * previews so the same stored document renders consistently.
 */
import type { ProfileEntry, ProfileLine } from "~vue/components/ProfileTopicSection.vue";
import { DEFAULT_ICON, SUBTOPIC_ICONS } from "~vue/utils/collabIcons";

function splitContentLines(content: string): ProfileLine[] {
    return content
        .split(/\r?\n/)
        .map((line) => {
            const trimmed = line.trim();
            const isBullet = /^[-•]\s*/.test(trimmed);
            return { text: isBullet ? trimmed.replace(/^[-•]\s*/, "") : trimmed, isBullet };
        })
        .filter((line) => Boolean(line.text));
}

export interface ProfileSubtopicSource {
    id: string;
    title: string;
    content: string;
    placeholder?: string;
}

export function toProfileEntry({ id, title, content, placeholder = "" }: ProfileSubtopicSource): ProfileEntry {
    const trimmed = content.trim();
    const lines = trimmed ? splitContentLines(trimmed) : [];
    return {
        id,
        title,
        content: trimmed,
        // Single-line content previously rendered an unwanted bullet marker where the text was intended as
        // a sentence.
        lines: lines.length > 1 ? lines : trimmed ? [{ text: trimmed, isBullet: false }] : [],
        icon: SUBTOPIC_ICONS[title] ?? DEFAULT_ICON,
        placeholder,
    };
}
